<template>
    <StepShell :embedded="embedded" layout="page" width="narrow" gap="none">
        <BasePageHeader :variant="embedded ? 'step' : 'page'" :title="headlineCopy" :subtitle="showFailure ? undefined : LEDE" no-border subtitle-below>
            <template v-if="showFailure" #eyebrow>
                <p class="eyebrow">
                    <i aria-hidden="true" class="bi bi-exclamation-circle"></i>
                    {{ eyebrowCopy }}
                </p>
            </template>
        </BasePageHeader>

        <!-- PerspectiveResultStep already presents the archetype; keep this screen focused on revised
             readings rather than repeating the personality result.
             -->

        <!-- The second pass saves readings independently as parallel jobs finish, so show each result
             immediately.
             -->
        <ReadingList v-if="cards.length > 0 || pendingSlots > 0" class="readings" :readings="cards" :pending-count="pendingSlots" :previous-titles="previousTitles" />

        <BaseNotification v-else theme="warning" hide-dismiss class="notice">
            <template #content>
                <div class="notice-body">
                    <p class="notice-head">{{ noticeHeadline }}</p>
                    <p class="notice-copy">{{ noticeCopy }}</p>
                    <p class="notice-reassurance">Your answers are saved and already shaping how I coach you.</p>
                </div>
            </template>
        </BaseNotification>

        <!-- The linear flow explains the profile here; coaching setup has a
             separate introduction before entering the profile screen. -->
        <section v-if="!embedded" class="handoff">
            <h2 class="handoff-title">One last thing: your collaboration profile</h2>
            <p class="handoff-body">{{ handoffCopy }}</p>
        </section>

        <!-- Keep profile skipping on the profile screen; this screen only presents results. -->
        <StepActions>
            <BaseButton theme="primary" @click="emit('continue')">{{ embedded ? "Continue" : "View my profile" }}</BaseButton>
        </StepActions>
    </StepShell>
</template>

<script setup lang="ts">
import ReadingList from "~vue/CalendarFirstOnboarding/ReadingList.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BasePageHeader from "~vue/components/BasePageHeader.vue";
import { computed } from "vue";

import StepActions from "./StepActions.vue";
import StepShell from "./StepShell.vue";
import type { AccessLevel, ReadingsState } from "./types";

const {
    embedded = false,
    readings,
    settled,
    availableAccessLevels,
    calendarConnected = true,
    firstPassReadings = null,
} = defineProps<{
    embedded?: boolean;
    readings: ReadingsState | null;
    /** False while readings are still landing one at a time. */
    settled: boolean;
    /** Empty of anything but `private` when the org has publishing turned off. */
    availableAccessLevels: AccessLevel[];
    /** False means readings were generated from assessment answers without calendar context. */
    calendarConnected?: boolean;
    /** The first pass, so each reading can show the headline it revised. */
    firstPassReadings?: ReadingsState | null;
}>();

const emit = defineEmits<{ continue: [] }>();

/* An org with publishing off must not be promised a "who sees it" choice the
   publish screen will not offer. */
const canPublish = computed(() => availableAccessLevels.some((level) => level !== "private"));

/* Written, not promised: generation starts when the assessment completes, so by
   the time this screen is on show the profile usually already exists. */
const handoffCopy = computed(() =>
    canPublish.value
        ? "A short guide to working with you, written from your answers — what a new colleague would otherwise take months to learn. Read it first, then choose who sees it."
        : "A short guide to working with you, written from your answers. Only you can see it, and I’ll use it when I coach you.",
);

/** Keyed by category, which is what pairs a second reading with the first. */
const previousTitles = computed(() => Object.fromEntries((firstPassReadings?.readings ?? []).map((reading) => [reading.category, reading.title])));

/* Hide this lede when reading generation fails; it promises insights the screen cannot provide. */
const LEDE = "Thanks for telling me how you like to work. Now I can deliver better insights about your week.";

const READING_SLOT_COUNT = 3;

/*
 * Unlike the first pass, a sparse calendar is not disqualifying here: these
 * readings are driven by the answers the user just gave, which is the whole
 * claim of the screen. A run that failed part-way keeps whatever landed before
 * it broke: those readings are real and already written against the answers, and
 * discarding them for an apology would lose the user something we have.
 */
const cards = computed(() => readings?.readings ?? []);

const hasReadings = computed(() => cards.value.length > 0);

/* Slots still to be filled, held open only while more can still arrive. */
const pendingSlots = computed(() => (settled ? 0 : Math.max(READING_SLOT_COUNT - cards.value.length, 0)));

/* Nothing arrived and nothing more is coming — the only state the apology fits. */
const showFailure = computed(() => settled && !hasReadings.value);

/*
 * "It broke" and "it is still running and you have waited long enough" are
 * different things to be told, and only the first is worth an apology. The page
 * lands here on either, so the distinction has to be drawn from the status: a
 * terminal `failed` is a real error, anything still mid-flight is the deadline.
 */
type Failure = "failed" | "timed_out" | "empty";

const failure = computed<Failure>(() => {
    if (readings === null) {
        return "timed_out";
    }
    if (readings.status === "failed") {
        return "failed";
    }
    if (readings.status === "not_started" || readings.status === "running") {
        return "timed_out";
    }
    return "empty";
});

const eyebrowCopy = computed(() => (failure.value === "failed" ? "Something went wrong on my side" : "The second read didn’t arrive"));

const headlineCopy = computed(() => {
    if (showFailure.value) {
        return "Your answers are in — the second read is not";
    }
    return calendarConnected ? "Now I can tell you why your week is shaped like this" : "Here’s what your answers tell me about how you work";
});

const noticeHeadline = computed(() => {
    if (failure.value === "failed") {
        return "The second read failed";
    }
    return failure.value === "empty" ? "The second read had nothing to add" : "The second read is running long";
});

const noticeCopy = computed(() => {
    if (failure.value === "failed") {
        return "Re-reading your calendar against your answers hit an error on my side, so there’s nothing sharper to show you.";
    }
    if (failure.value === "empty") {
        return "I re-read your calendar against your answers and didn’t find anything sharper than what you’ve already seen.";
    }
    return "It’s still running, and I won’t make you wait for it. Nothing after this depends on it.";
});
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .eyebrow {
        @apply details tracking-wide uppercase inline-flex items-center gap-2 text-ink-muted;
    }

    .readings {
        @apply mt-2 w-full;
    }

    .notice {
        @apply mt-7 w-full items-start px-5 py-4 text-left;
    }

    .notice-body {
        @apply min-w-0;
    }

    .notice-head {
        @apply body-small-bold;
    }

    .notice-copy {
        @apply body-small mt-1.5;
    }

    .notice-reassurance {
        @apply body-small mt-3 border-t border-notification-warning-border pt-3;
    }

    .handoff {
        @apply mt-6 w-full border-t border-border pt-4;
    }

    .handoff-title {
        @apply body-semibold text-ink;
    }

    .handoff-body {
        @apply body-small mt-2 text-ink;
    }
}
</style>
